import { keepPreviousData, QueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { mediaManagerClient } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { PaginatedResponse, ParamsWithPagination } from '@/types/api';
import { normalizeIds, requestInChunks } from '@/utils/ids';

import { LogosFilters, TLogo, TLogosAgregations } from '../types';

import { logosQueryKeys } from './query-keys';

/**
 * RISK: `limit` is required. Without one the endpoint answers its first 100 logos, and a
 * caller reading "the catalogue" that way silently drops every older logo.
 */
type LogosRequestParams = ParamsWithPagination<LogosFilters> & { limit: number };

/**
 * Page size of the logo filter's infinite list. Matches the 25 of `useInfiniteClips`; the
 * dropdown viewport shows roughly six rows, so one request covers several screens of scroll.
 */
export const LOGOS_FILTER_PAGE_SIZE = 25;

/**
 * `name` is not unique, and the endpoint pages by slicing `offset:offset+limit`. Without a
 * tiebreaker the order is not total, so a logo can land on two consecutive pages or on none.
 */
export const LOGOS_FILTER_ORDER_BY = 'name asc,id asc';

export async function getLogos(
  params: LogosRequestParams
): Promise<PaginatedResponse<TLogo, TLogosAgregations>> {
  return mediaManagerClient.get('api/v1/logos', {
    params: {
      limit: params.limit,
      search: params.search,
      offset: params.offset,
      id__in: params.idIn?.join(','),
      labels__in: params.labelsIn?.length ? params.labelsIn.join(',') : undefined,
      name: params.name,
      updated_by__in: params.updatedByIn?.join(','),
      state__in: params.stateIn?.join(','),
      order_by: params.order_by,
      segment_start_datetime__gte: params.segmentStartDatetimeGte,
      segment_end_datetime__lte: params.segmentEndDatetimeLte,
      clip_start_datetime__gte: params.clipStartDatetimeGte,
      clip_end_datetime__lte: params.clipEndDatetimeLte,
    },
  });
}

type QueryFnType = typeof getLogos;

interface UseLogosParams {
  config?: QueryConfig<QueryFnType>;
  params: LogosRequestParams;
}

export function useLogos({ config, params }: UseLogosParams) {
  return useQuery({
    ...config,
    queryKey: logosQueryKeys.list(params),
    queryFn: () => getLogos(params),
  });
}

export type LogosFilterParams = Pick<
  ParamsWithPagination<LogosFilters>,
  | 'search'
  | 'labelsIn'
  | 'segmentStartDatetimeGte'
  | 'segmentEndDatetimeLte'
  | 'clipStartDatetimeGte'
  | 'clipEndDatetimeLte'
>;

/**
 * The logo catalogue, one page at a time, ordered by name. Changing `search` or `labelsIn`
 * changes the query key, so react-query restarts paging from offset 0 on its own.
 */
export function useInfiniteLogos(
  params: LogosFilterParams = {},
  config: { staleTime?: number; enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    ...config,
    queryKey: logosQueryKeys.infiniteList(params),
    queryFn: ({ pageParam = 0 }) =>
      getLogos({
        ...params,
        offset: pageParam,
        limit: LOGOS_FILTER_PAGE_SIZE,
        order_by: LOGOS_FILTER_ORDER_BY,
      }),
    initialPageParam: 0,
    // Without it the dropdown empties for a round-trip on every settled keystroke.
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.data.length === LOGOS_FILTER_PAGE_SIZE
        ? allPages.reduce((total, page) => total + page.data.length, 0)
        : undefined,
  });
}

/** Stores a logo the client already holds, e.g. one just created, as the answer for its id. */
export function seedSelectedLogo(queryClient: QueryClient, logo: TLogo) {
  queryClient.setQueryData(logosQueryKeys.selected([logo.id]), { data: [logo], total: 1 });
}

/**
 * Resolves a selection of logo ids to logos, whichever page of the catalogue they belong to.
 * Without it a selected logo outside the loaded pages loses its name, its chip and its place
 * in the selection count.
 *
 * `keepPrevious` keeps the previous answer while a changed set loads, for views that list
 * rows. It is opt-in: the toolkit sidebar badge treats any data as "resolved", and with the
 * previous answer it would count a new segment against the previous segment's logos.
 * `initialData` is the answer when the caller already holds every logo, e.g. from a loaded page.
 */
export function useSelectedLogos(
  ids: string[],
  {
    keepPrevious = false,
    initialData,
  }: { keepPrevious?: boolean; initialData?: PaginatedResponse<TLogo, TLogosAgregations> } = {}
) {
  return useQuery({
    enabled: ids.length > 0,
    initialData,
    queryKey: logosQueryKeys.selected(ids),
    queryFn: async () => {
      const pages = await requestInChunks(normalizeIds(ids), idIn =>
        getLogos({ idIn, limit: idIn.length })
      );

      return {
        ...pages[0],
        data: pages.flatMap(page => page.data),
        total: pages.reduce((n, page) => n + page.total, 0),
      };
    },
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  });
}
